import { ButtonBar, useSnackbar } from "@dain/mui-components";
import { useCallback, useEffect, useRef, useState } from "react";
import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import { SearchButton } from "../../common/button/CommonButtons";
import { CommonTranslKey } from "../../common/CommonTranslKey";
import { PageableResultListDto } from "../../client/pagination/PageableResultListDto";
import {SortingOrderType} from "../../client/pagination/SortingFilterDto";
import {useClientLocator} from "../../client/ApiClientLocator";
import {PageLoadSize} from "../../common/pagination/PageableGrid";
import {IsdsMessageStateDto, IsdsMessageStateFilterDto} from "../../client/message-state/IsdsMessageStateApiClient";
import MessageStateGrid from "./MessageStateGrid";
import {MessageStateFilterForm} from "./MessageStateFilterForm";

enum SearchPanelState {
    INITIALIZED,
    IDLE,
    SEARCH_IN_PROGRESS
}

export enum FilterSearchType {
    NEW_SEARCH,
    NEXT_PAGE_SEARCH
}

export const DEFAULT_MESSAGE_STATE_FILTER: IsdsMessageStateFilterDto = {
    paging: {
        maxPageSize: PageLoadSize.INITIAL_PAGE_SIZE,
        pageToken: null,
    },
    sorting: {
        sortingCriteria: [
            { property: "state.changeTime", order: SortingOrderType.DESC },
        ],
    },
};

export default function MessageStateSearchPanel() {
    const {isdsMessageStateClient} = useClientLocator();
    const snackbar = useSnackbar();
    const {t} = useTranslation();

    const [panelStatus, setPanelStatus] = useState<SearchPanelState>(SearchPanelState.INITIALIZED);
    const [stateFilter, setStateFilter] = useState<IsdsMessageStateFilterDto>(DEFAULT_MESSAGE_STATE_FILTER);

    const [stateResult, setStateResult] = useState<PageableResultListDto<IsdsMessageStateDto>>({
        items: [],
        nextPageToken: null,
    });

    const [displayedStatesCount, setDisplayedStatesCount] = useState<number>(0);
    const isInitialized = useRef(false);

    function onFilterChanged(updatedFilter: IsdsMessageStateFilterDto) {
        setStateFilter(updatedFilter);
    }

    const updatePaginatedMessage = (newFetchedMessageStates: PageableResultListDto<IsdsMessageStateDto>, existingMessageStatesResults: PageableResultListDto<IsdsMessageStateDto>, searchType: FilterSearchType):PageableResultListDto<IsdsMessageStateDto> => {
        if (searchType === FilterSearchType.NEW_SEARCH) {
            return newFetchedMessageStates;
        } else if (searchType === FilterSearchType.NEXT_PAGE_SEARCH) {
            return {
                items: [...existingMessageStatesResults.items, ...newFetchedMessageStates.items],
                nextPageToken: newFetchedMessageStates.nextPageToken || null
            };
        }

        return {
            items: [],
            nextPageToken: null
        };
    };

    const fetchMessageStates = useCallback((filter: IsdsMessageStateFilterDto, searchType: FilterSearchType) => {
            setPanelStatus(SearchPanelState.SEARCH_IN_PROGRESS);

            isdsMessageStateClient.findByFilter(filter)
                .then((filteredMessageStatesPage) => {
                    const updatedMessages = updatePaginatedMessage(filteredMessageStatesPage, stateResult, searchType);
                    console.log(filteredMessageStatesPage);
                    setStateResult(updatedMessages);
                })
                .catch((error) => {
                    snackbar.error(t(CommonTranslKey.UNEXPECTED_ERROR), error);
                })
                .finally(() => {
                    setPanelStatus(SearchPanelState.IDLE);
                });
        },
        [isdsMessageStateClient, snackbar, stateResult, t]
    );

    const countByFilter = useCallback((filter: IsdsMessageStateFilterDto) => {
            setPanelStatus(SearchPanelState.SEARCH_IN_PROGRESS);
            return isdsMessageStateClient.countByFilter(filter)
                .then((filteredMessageStatesPage) => {
                    setDisplayedStatesCount(filteredMessageStatesPage);
                })
                .catch((error) => snackbar.error(t(CommonTranslKey.UNEXPECTED_ERROR), error))
                .finally(() => setPanelStatus(SearchPanelState.IDLE));
        },
        [isdsMessageStateClient, snackbar, t]
    );

    function resetPagingFilter(messageFilter: IsdsMessageStateFilterDto, defaultMessageFilter: IsdsMessageStateFilterDto) {
        messageFilter.paging = defaultMessageFilter.paging;
    }

    const resetAndSearchMessages = useCallback(() => {
            setStateResult({ items: [], nextPageToken: null });
            setDisplayedStatesCount(0);

            resetPagingFilter(stateFilter, DEFAULT_MESSAGE_STATE_FILTER);

            fetchMessageStates(stateFilter, FilterSearchType.NEW_SEARCH);
            countByFilter(stateFilter);
        },[fetchMessageStates, countByFilter, stateFilter],
    );

    useEffect(() => {
        if (!isInitialized.current) {
            isInitialized.current = true;
            resetAndSearchMessages();
        }
    }, [fetchMessageStates, countByFilter, resetAndSearchMessages]);

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <MessageStateFilterForm filter={stateFilter} onChange={onFilterChanged} />
            </Grid>
            <Grid item xs={12}>
                <ButtonBar>
                    <SearchButton onClick={resetAndSearchMessages} />
                </ButtonBar>
            </Grid>
            <Grid item xs={12}>
                <Grid container>
                    <Grid item xs={12}>
                        {panelStatus !== SearchPanelState.INITIALIZED && (
                            <MessageStateGrid
                                rowsData={stateResult.items}
                                totalMessageCount={displayedStatesCount}

                                nextPageToken={stateResult.nextPageToken}
                                messageFilter={stateFilter}
                                fetchMessages={fetchMessageStates}
                            />
                        )}
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
}
